import { type FC, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/utils/core";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { supabase } from "@/shared/lib/supabase";
import { ONBOARDING_ROUTES, LAWYER_ROUTES, AUTH_ROUTES } from "@/shared/constants/paths";
import { setCachedLawyerStatus } from "@/shared/hooks/useIsLawyer";
import { clearOnboardingData } from "@/shared/utils/onboarding";
import { EventType, trackEvent } from "@/shared/utils/core";
import { sentryUtils } from "@/shared/utils/sentry";
import { toUser } from "@/shared/types/auth";
import { useAuthRouter } from "@/shared/hooks/useAuthRouter";
import { useGoogleOAuthEnabled } from "@/shared/hooks/useGoogleOAuthEnabled";
import {
  startGoogleOAuth,
  getGoogleOAuthContext,
  clearGoogleOAuthContext,
} from "@/shared/utils/googleOAuth";
import { saveLastAuthMethod, getLastAuthMethod } from "@/shared/utils/lastAuthMethod";
import { isValidEmail } from "@/shared/utils/validators";
import logoFull from "@/shared/assets/logo/logo-full.svg";
import googleIcon from "@/shared/assets/icons/google.svg";

import { getOrderedStories } from "@/pages/onboarding/authStories";
import { StoryCarousel } from "@/pages/onboarding/components/StoryCarousel";

interface LoginAttemptResult {
  locked: boolean;
  lockedUntil: string | null;
  attemptsRemaining: number | null;
  failedAttempts: number;
}

const validateEmail = (value: string): string | null => {
  if (!value) return "Enter your email address";
  if (!isValidEmail(value)) return "Enter a valid email address";
  return null;
};

const validatePassword = (value: string): string | null => {
  if (!value) return "Enter your password";
  if (value.length < 6) return "Password must be at least 6 characters";
  return null;
};

export const LoginPage: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
  const [lockedEmail, setLockedEmail] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<string | null>(null);

  const { destination, isLoading: isCheckingAuth, route, refetch } = useAuthRouter();

  useEffect(() => {
    if (!lockedUntil) {
      setCountdown(null);
      return;
    }

    const tick = () => {
      const remaining = lockedUntil.getTime() - Date.now();
      if (remaining <= 0) {
        setLockedUntil(null);
        setLockedEmail(null);
        setCountdown(null);
        return;
      }
      const totalSeconds = Math.ceil(remaining / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      setCountdown(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const postLoginAttempt = useCallback(
    async (
      payload: { success?: boolean; check?: boolean },
      accessToken?: string,
    ): Promise<LoginAttemptResult | null> => {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        apikey: supabaseAnonKey,
      };
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const res = await fetch(`${supabaseUrl}/functions/v1/security/login-attempt`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email, ...payload }),
      });

      if (!res.ok) {
        console.error("login-attempt endpoint error:", res.status, await res.text());
        return null;
      }

      const data = (await res.json()) as LoginAttemptResult;

      if (data.locked && data.lockedUntil) {
        setLockedUntil(new Date(data.lockedUntil));
        setLockedEmail(email);
      }

      return data;
    },
    [email],
  );
  const isGoogleOAuthEnabled = useGoogleOAuthEnabled();
  const lastAuthMethod = getLastAuthMethod();

  const storyParam = searchParams.get("story");
  const orderedStories = useMemo(() => getOrderedStories(storyParam), [storyParam]);

  const [oauthVerifying, setOauthVerifying] = useState(() => {
    const ctx = getGoogleOAuthContext();
    return ctx?.type === "login";
  });

  const oauthHandled = useRef(false);

  useEffect(() => {
    if (!oauthVerifying || isCheckingAuth || destination.type === "login") return;
    if (oauthHandled.current) return;
    oauthHandled.current = true;

    const verifyAccount = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user?.email) {
        clearGoogleOAuthContext();
        setOauthVerifying(false);
        return;
      }

      const { data } = await supabase.functions.invoke("communications/email/exists", {
        body: { email: user.email.toLowerCase() },
      });

      if (!data?.exists) {
        const authId = user.id;
        await supabase.auth.signOut();
        supabase.functions
          .invoke("account/delete-auth-user", {
            body: { authId },
          })
          .catch((err: unknown) => {
            sentryUtils.captureError(err, "Failed to delete auth user during OAuth mismatch");
          });
        clearGoogleOAuthContext();
        setError(
          "We couldn't find an account with this email. Check the spelling, or create an account.",
        );
        await refetch();
        setOauthVerifying(false);
        return;
      }

      clearGoogleOAuthContext();
      saveLastAuthMethod("google");
      trackEvent(EventType.USER_LOGGED_IN, {
        login_method: "google",
        is_returning_user: true,
      });
      setOauthVerifying(false);
      navigate(route, { replace: true });
    };

    verifyAccount();
  }, [oauthVerifying, isCheckingAuth, destination, route, navigate, refetch]);

  useEffect(() => {
    if (isCheckingAuth || oauthVerifying || isLoading) return;

    if (destination.type !== "login") {
      navigate(route, { replace: true });
    }
  }, [isCheckingAuth, oauthVerifying, isLoading, destination, route, navigate]);

  const isLockedForCurrentEmail = !!lockedUntil && lockedEmail === email;

  const handleSubmit = async () => {
    const emailErr = validateEmail(email);
    const passwordErr = validatePassword(password);
    setEmailError(emailErr);
    setPasswordError(passwordErr);
    if (emailErr || passwordErr || isLoading || isLockedForCurrentEmail) return;

    setIsLoading(true);
    setError(null);

    try {
      const preflight = await postLoginAttempt({ check: true });
      if (preflight?.locked) {
        trackEvent(EventType.ACCOUNT_LOCKED_OUT, {
          failed_attempts: preflight.failedAttempts,
          locked: true,
        });
        return;
      }

      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        const result = await postLoginAttempt({ success: false });
        const failedAttempts = result?.failedAttempts ?? 0;
        const locked = result?.locked ?? false;

        if (locked) {
          trackEvent(EventType.ACCOUNT_LOCKED_OUT, { failed_attempts: failedAttempts, locked });
        } else {
          trackEvent(EventType.LOGIN_FAILED, { failed_attempts: failedAttempts, locked });
          setError(authError.message);
        }
        return;
      }

      if (data.user) {
        postLoginAttempt({ success: true }, data.session?.access_token).catch(() => {});
        queryClient.setQueryData(["auth", "user"], toUser(data.user));
        clearOnboardingData();

        const { data: dbUser } = await supabase
          .from("users")
          .select("user_id")
          .eq("auth_id", data.user.id)
          .single();

        if (dbUser) {
          const { data: lawyer } = await supabase
            .from("lawyers")
            .select("id")
            .eq("user_id", dbUser.user_id)
            .maybeSingle();

          if (lawyer) {
            setCachedLawyerStatus(true);
            saveLastAuthMethod("password");
            trackEvent(EventType.USER_LOGGED_IN, {
              login_method: "email",
              is_returning_user: true,
              user_type: "lawyer",
            });
            navigate(LAWYER_ROUTES.DASHBOARD);
            return;
          }
        }

        saveLastAuthMethod("password");
        trackEvent(EventType.USER_LOGGED_IN, {
          login_method: "email",
          is_returning_user: true,
        });

        await refetch();
      }
    } catch (err) {
      setError("We couldn't log you in. Try again in a moment.");
      sentryUtils.captureError(err, "Unexpected login error");
      sentryUtils.logAuthenticationEvent("login");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    const oauthError = await startGoogleOAuth({
      type: "login",
      redirectPath: AUTH_ROUTES.SIGN_IN,
    });
    if (oauthError) {
      setError(oauthError);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-subtle">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-subtle">
      <div className="flex flex-1 flex-col overflow-y-auto px-8 py-8">
        <img src={logoFull} alt="Elayne" className="h-7 self-start" />

        <div className="flex flex-1 items-center justify-center">
          <div className="flex w-full max-w-md flex-col gap-5">
            <h2 className="mb-2 text-center">Welcome back</h2>

            {isGoogleOAuthEnabled && (
              <>
                <div className="relative">
                  <Button
                    variant="outline"
                    rounded={true}
                    className="w-full gap-2"
                    onClick={handleGoogleSignIn}
                  >
                    <img src={googleIcon} alt="Google" className="h-4 w-4" />
                    Continue with Google
                  </Button>
                  {lastAuthMethod === "google" && (
                    <span className="absolute -right-2 -top-2.5 rounded-sm bg-inverse px-1.5 text-2xs text-white">
                      Last used
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  <div className="h-px flex-1 bg-stone-200" />
                  <span className="text-sm text-secondary">or</span>
                  <div className="h-px flex-1 bg-stone-200" />
                </div>
              </>
            )}

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="email" className="text-sm font-medium text-primary">
                  Email address
                </label>
                {lastAuthMethod === "password" && (
                  <span className="rounded-sm bg-inverse px-1.5 text-2xs text-white">
                    Last used
                  </span>
                )}
              </div>
              <Input
                id="email"
                type="email"
                placeholder="example@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                  setEmailError(null);
                }}
                onBlur={() => setEmailError(validateEmail(email))}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                className={emailError ? "border-destructive" : ""}
              />
              <FieldError message={emailError} />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="password" className="text-sm font-medium text-primary">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                    setPasswordError(null);
                  }}
                  onBlur={() => setPasswordError(validatePassword(password))}
                  onKeyDown={handleKeyDown}
                  className={cn("pr-10", passwordError && "border-destructive")}
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <FieldError message={passwordError} />
              <Link to={AUTH_ROUTES.FORGOT_PASSWORD} className="text-sm text-primary underline">
                Forgot password?
              </Link>
            </div>

            {countdown && isLockedForCurrentEmail ? (
              <p className="text-sm text-destructive">
                Too many failed attempts. Try again in {countdown}.
              </p>
            ) : (
              <FieldError message={error} />
            )}

            <Button
              variant="brand"
              rounded={true}
              onClick={handleSubmit}
              disabled={isLoading || isLockedForCurrentEmail}
              className="w-full"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Log in <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>

            <p className="text-center text-sm text-secondary">
              New to Elayne?{" "}
              <Link to={ONBOARDING_ROUTES.ACCOUNT} className="text-brand underline">
                Create account
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex lg:w-1/3 p-5 pl-0">
        <StoryCarousel stories={orderedStories} className="flex-1" />
      </div>
    </div>
  );
};
